import axios from 'axios';
import { config } from '../config/config';
import { getAuthToken } from './auth';
import { errorDetailMessage } from '../utils/apiError';

const api = axios.create({
  baseURL: `${config.API_BASE_URL}/api/v1`,
  timeout: 60000
});

// Auto-attach Firebase Bearer token to every request
api.interceptors.request.use(async (reqConfig) => {
  const token = await getAuthToken();
  if (token) reqConfig.headers.Authorization = `Bearer ${token}`;
  return reqConfig;
});

// Redirect to /login on 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Extract a readable message from an API error (string, object or list detail).
export function apiErrorMessage(err, fallback = 'Something went wrong') {
  return errorDetailMessage(err, fallback);
}

// --- Organization ---
export const orgApi = {
  getUserOrganizations: (userId) => api.get(`/users/${userId}/organizations`),
  getOrgById: (orgId) => api.get(`/organizations/${orgId}`),
  createOrganization: (data) => api.post('/organizations', data),
  getDashboard: (orgId) => api.get(`/organizations/${orgId}/dashboard`),
  // Which parts of the student app this institute exposes.
  updateFeatures: (orgId, data) => api.patch(`/organizations/${orgId}/features`, data),
  // Gives the owner's account the claim Storage rules check before a write.
  // Called through services/storageAccess.js, not directly.
  ensureStorageAccess: (orgId) => api.post(`/organizations/${orgId}/storage-access`),
  // Logo and tagline shown to students. Stored on the org's metadata.
  updateBranding: (orgId, data) => api.patch(`/organizations/${orgId}/branding`, data)
};

// --- Exam Library (structural metadata) ---
export const examLibraryApi = {
  // Lightweight org-scoped catalogue (avoids heavy /admin/exams bulk load)
  catalogue: (orgId) => api.get(`/organizations/${orgId}/exams/catalogue`),
  enrolled: (orgId) => api.get(`/organizations/${orgId}/exams`),
  enroll: (orgId, examId) => api.post(`/organizations/${orgId}/exams/enroll`, { examId }),
  // The routes below are owner-only and answer 404 for an exam the org has not enrolled.
  getDetail: (orgId, examId) => api.get(`/organizations/${orgId}/exams/${examId}`),
  // Sections with their topics and per-topic question counts, in one call.
  getStructure: (orgId, examId) => api.get(`/organizations/${orgId}/exams/${examId}/structure`)
};

// --- Question Banks (question pools within enrolled exams) ---
export const bankApi = {
  // `params` is { examId, topicId?, limit? }; the server defaults limit to 200 (max 1000).
  getQuestions: (orgId, params) => api.get(`/organizations/${orgId}/questions`, { params })
};

// --- Question Bank Uploads & ETL (B2B channel) ---
export const bankUploadApi = {
  upload: (orgId, formData) => api.post(`/organizations/${orgId}/question-banks/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listBanks: (orgId) => api.get(`/organizations/${orgId}/question-banks`),
  getJob: (orgId, jobId) => api.get(`/organizations/${orgId}/etl/jobs/${jobId}`),
  getJobQuestions: (orgId, jobId) => api.get(`/organizations/${orgId}/etl/jobs/${jobId}/questions`),
  updateJobQuestion: (orgId, jobId, questionId, body) => api.patch(`/organizations/${orgId}/etl/jobs/${jobId}/questions/${questionId}`, body),
  // 409 { detail: { code: 'needs_review', needsReviewCount, message } } while AI-guessed
  // answers are unreviewed, unless `force` is set (see utils/apiError.js).
  publish: (orgId, jobId, { force = false } = {}) =>
    api.post(`/organizations/${orgId}/etl/jobs/${jobId}/publish`, null, force ? { params: { force: true } } : undefined),
  unpublish: (orgId, jobId) => api.post(`/organizations/${orgId}/etl/jobs/${jobId}/unpublish`),
  // 409 when the job is not in a retryable state; the detail says why.
  retryJob: (orgId, jobId) => api.post(`/organizations/${orgId}/etl/jobs/${jobId}/retry`)
};

// --- Quizzes ---
export const quizApi = {
  list: (orgId) => api.get(`/organizations/${orgId}/quizzes`),
  // Who took a test, who did not, and where the batch struggled.
  results: (orgId, quizId) => api.get(`/organizations/${orgId}/quizzes/${quizId}/results`),
  // After launch. The server decides what may change; utils/quizActions.js mirrors it.
  close: (orgId, quizId) => api.post(`/organizations/${orgId}/quizzes/${quizId}/close`),
  // `data` holds only the changed fields: name, opensAt, closesAt, durationMinutes, allowedAttempts.
  update: (orgId, quizId, data) => api.patch(`/organizations/${orgId}/quizzes/${quizId}`, data),
  remove: (orgId, quizId) => api.delete(`/organizations/${orgId}/quizzes/${quizId}`),
  quickLaunch: (orgId, data) => api.post(`/organizations/${orgId}/quizzes/launch`, data),
  screenerLaunch: (orgId, data) => api.post(`/organizations/${orgId}/quizzes/screener`, data)
};

// --- Students (org members with role: student) ---
export const studentApi = {
  list: (orgId) => api.get(`/organizations/${orgId}/students`),
  invite: (orgId, data) => api.post(`/organizations/${orgId}/students/invite`, data),
  remove: (orgId, studentId) => api.delete(`/organizations/${orgId}/students/${studentId}`),
  // Quizzes are addressed by batch, so a student with none can be assigned nothing.
  setBatch: (orgId, studentId, batchId) =>
    api.patch(`/organizations/${orgId}/students/${studentId}/batch`, { batchId }),
  // One student's history across this institute's tests.
  attempts: (orgId, studentId) => api.get(`/organizations/${orgId}/students/${studentId}/attempts`),
  // Students who joined with the institute code wait for an owner to approve.
  approve: (orgId, studentId) => api.post(`/organizations/${orgId}/requests/${studentId}/approve`),
  reject: (orgId, studentId) => api.post(`/organizations/${orgId}/requests/${studentId}/reject`)
};

// --- Batches ---
export const batchApi = {
  list: (orgId) => api.get(`/organizations/${orgId}/batches`),
  // `data` is { name, examId } — a batch is bound to the exam it prepares for.
  create: (orgId, data) => api.post(`/organizations/${orgId}/batches`, data),
  update: (orgId, batchId, data) => api.patch(`/organizations/${orgId}/batches/${batchId}`, data)
};

// --- Content / Notes ---
export const contentApi = {
  list: (orgId) => api.get(`/organizations/${orgId}/content`),
  publish: (orgId, data) => api.post(`/organizations/${orgId}/content`, data)
};

// --- Analytics (reuse existing — already has B2B org-member auth) ---
export const analyticsApi = {
  studentExam: (userId, examId) => api.get(`/analytics/users/${userId}/exams/${examId}/analytics`),
  studentCharts: (userId, examId, params) => api.get(`/analytics/users/${userId}/exams/${examId}/charts`, { params })
};

export default api;
